/**
 * Quill and quill-table-up touch `document` at module load — this file is only loaded via dynamic
 * `import()` from `editor.tsx` inside `oncreate` (browser), so SSR never evaluates it.
 */
import type m from '@bitstillery/mithril'
import type Quill from 'quill'
import {modelref_adapter, modelref_assign} from '@bitstillery/common/lib/store'

import type {FieldEditorAttrs} from '@bitstillery/common/types/field'

export interface FieldEditorQuillHost {
    editor?: Quill
    placeholder_plugin?: {get_clean_html: () => string} | undefined
    _change_from_quill: boolean
    _applying_placeholder_format: boolean
}

let quill_resize_register_promise: Promise<void> | null = null

async function ensure_quill_resize_registered(Quill: {register: (path: string, def: unknown) => void}): Promise<void> {
    if (typeof HTMLElement === 'undefined') {
        return
    }
    if (!quill_resize_register_promise) {
        quill_resize_register_promise = import('quill-resize-image').then((mod) => {
            Quill.register('modules/resize', mod.default)
        })
    }
    await quill_resize_register_promise
}

let did_register_quill_modules = false

export async function init_field_editor_quill(
    self: FieldEditorQuillHost,
    vnode: m.VnodeDOM<FieldEditorAttrs>,
    editor_element: HTMLElement,
    toolbar_options: unknown[],
): Promise<void> {
    const Quill = (await import('quill')).default
    const {
        default: TableUp,
        TableAlign,
        TableMenuContextmenu,
        TableResizeBox,
        TableResizeScale,
        TableSelection,
        TableVirtualScrollbar,
        defaultCustomSelect,
    } = await import('quill-table-up')
    const {PlaceholderPlugin} = await import('./plugins/placeholders')
    const {ImageBlot} = await import('./plugins/images')

    await ensure_quill_resize_registered(Quill)

    if (!did_register_quill_modules) {
        Quill.register({
            [`modules/${TableUp.moduleName}`]: TableUp,
            'formats/image': ImageBlot,
            'modules/placeholders': PlaceholderPlugin,
        })
        did_register_quill_modules = true
    }

    const modules: Record<string, unknown> = {
        [TableUp.moduleName]: {
            align: TableAlign,
            customSelect: defaultCustomSelect,
            resize: TableResizeBox,
            resizeScale: TableResizeScale,
            scrollbar: TableVirtualScrollbar,
            selection: TableSelection,
            selectionOptions: {
                tableMenu: TableMenuContextmenu,
            },
        },
        toolbar: {
            container: toolbar_options,
            handlers: vnode.attrs.handlers || {},
        },
        placeholders: {
            ...vnode.attrs.placeholder_options,
            on_placeholder_click: vnode.attrs.on_placeholder_click,
        },
    }
    if (typeof HTMLElement !== 'undefined') {
        modules.resize = {locale: {}}
    }

    const editor = new Quill(editor_element, {
        bounds: '.c-field-editor',
        modules: modules,
        theme: 'snow',
    })
    self.editor = editor

    const placeholder_module = editor.getModule('placeholders')
    const placeholder_plugin = placeholder_module instanceof PlaceholderPlugin ? placeholder_module : undefined
    self.placeholder_plugin = placeholder_plugin

    editor.on('text-change', (_delta, _oldDelta: unknown, _source: string) => {
        if (vnode.attrs.validation) {
            vnode.attrs.validation.dirty = true
        }
        const plain_value = placeholder_plugin ? placeholder_plugin.get_clean_html() : editor.getSemanticHTML()

        const {model_value, model_ref} = modelref_adapter(vnode.attrs.model)
        if (model_ref !== null && model_value !== plain_value) {
            self._change_from_quill = true
            modelref_assign(model_ref, plain_value)
            self._change_from_quill = false
        }

        if (_source === 'user' && !self._applying_placeholder_format) {
            self._applying_placeholder_format = true

            const has_line_break = _delta.ops.some(
                (op: {insert?: unknown}) => op.insert && typeof op.insert === 'string' && op.insert.includes('\n'),
            )

            const range = editor.getSelection()
            if (range && range.index > 0) {
                const last_char = editor.getText(range.index - 1, 1)
                if (last_char === ' ' || has_line_break) {
                    let target_line: {length: () => number} | undefined
                    let line_start = 0
                    let line_end = 0

                    if (has_line_break) {
                        const [prev_line] = editor.getLine(range.index - 1)
                        if (prev_line) {
                            target_line = prev_line
                            line_start = editor.getIndex(prev_line)
                            line_end = line_start + prev_line.length()
                        }
                    } else {
                        const [line] = editor.getLine(range.index)
                        if (line) {
                            target_line = line
                            line_start = editor.getIndex(line)
                            line_end = line_start + line.length()
                        }
                    }

                    if (target_line) {
                        const line_text = editor.getText(line_start, line_end - line_start)
                        const line_html = editor.getSemanticHTML(line_start, line_end - line_start)

                        if (/#[\w.=]+#/.test(line_text)) {
                            const ends_with_newline = editor.getText(line_end, 1) === '\n'
                            editor.deleteText(line_start, line_end - line_start)
                            editor.clipboard.dangerouslyPasteHTML(line_start, line_html + (ends_with_newline ? '<br>' : ''))

                            if (has_line_break) {
                                const new_line_end = line_start + target_line.length()
                                editor.setSelection(new_line_end)
                            } else {
                                const cursor_offset_in_line = range.index - line_start
                                editor.setSelection(line_start + cursor_offset_in_line)
                            }
                        }
                    }
                }
            }
            self._applying_placeholder_format = false
        }
    })

    const {model_value} = modelref_adapter(vnode.attrs.model)
    editor.setText('')
    editor.clipboard.dangerouslyPasteHTML(0, typeof model_value === 'string' ? model_value : '')
}
